<template>
	<div class="table-overflow-hide">
		<div class="directoryTable scrollable-55vh">
			<table class="table">
				<thead class="sticky-header">
					<tr>
						<th @click="sortBy('school_name')" class="sortable-header">
							School Name
							<span v-if="sortKey === 'school_name'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('district_name')" class="sortable-header">
							District
							<span v-if="sortKey === 'district_name'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('email')" class="sortable-header">
							Email
							<span v-if="sortKey === 'email'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('created_tstamp')" class="sortable-header">
							Created Date
							<span v-if="sortKey === 'created_tstamp'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('status')" class="sortable-header">
							Status
							<span v-if="sortKey === 'status'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
								</v-icon>
							</span>
						</th>
						<th class="icon-col">
							<v-btn
								@click="reload"
								icon
								color="gray200"
								size="x-small"
								:loading="trialsStore.loading"
								style="max-width: 20px; max-height: 20px"
							>
								<v-icon>mdi-reload</v-icon>
							</v-btn>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="trial in paginatedResults" :key="trial.trial_id" class="table-row">
						<td>{{ trial.school_name }}</td>
						<td>{{ trial.district_name }}</td>
						<td>{{ trial.email }}</td>
						<td>{{ formatDate(trial.created_tstamp) }}</td>
						<td>
							<v-chip :color="getStatusColor(trial.status)" size="small">
								{{ trial.status }}
							</v-chip>
						</td>
						<td>
							<v-btn
								color="gray300"
								density="compact"
								icon
								variant="text"
								@click="editTrial(trial)"
							>
								<v-icon color="gray500">mdi-note-edit</v-icon>
							</v-btn>
						</td>
					</tr>
				</tbody>
			</table>
			<div
				v-if="trialsStore.loading && !trialsStore.trials.length"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<LoadingState text="Loading Trials"></LoadingState>
			</div>
			<div
				v-if="!trialsStore.loading && !trialsStore.trials.length"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<div class="empty-state">
					<figure style="max-width: 300px">
						<IconListEmptyState />
					</figure>
					<div class="title">No trials found.</div>
					<p>Try adjusting your search or create a new trial.</p>
				</div>
			</div>
		</div>
	</div>
	<div class="table-pagination-btns">
		<pagination
			v-model:curr-page="currPage"
			v-model:per-page="perPage"
			:page-status-text="pageStatusText"
			:total-pages="totalPages"
			@page-down="pageDown"
			@page-up="pageUp"
		></pagination>
	</div>

	<!-- Edit Modal -->
	<TrialEditModal v-model="showEditModal" :trial="selectedTrial" />
</template>

<script setup lang="ts">
import { reactive, computed, watch, onMounted, ref } from "vue";
import usePaginate from "@/hooks/usePaginate";
import pagination from "@/components/pagination.vue";
import { complexSearch } from "@/utils";
import LoadingState from "@/components/loadingState.vue";
import IconListEmptyState from "@/assets/icons/list-empty-state.svg";
import dayjs from "dayjs";
import useTrials, { type TrialMembership } from "@/stores/support/trials";
import TrialEditModal from "@/components/support/TrialEditModal.vue";

const props = defineProps<{
  search: string;
  status: string;
}>();

defineEmits<{
  (e: 'update:search', ...args: any[]): void
}>();

const trialsStore = useTrials();

// Edit modal state
const showEditModal = ref(false);
const selectedTrial = ref<TrialMembership | null>(null);

// Sorting state
const sortKey = ref<string>('');
const sortOrder = ref<'asc' | 'desc'>('asc');

// Load trials on component mount
onMounted(() => {
  trialsStore.loadTrials({
    status: props.status
  });
});

// Watch for status changes and reload trials (search is handled client-side)
watch(() => props.status, (newStatus) => {
  trialsStore.loadTrials({
    status: newStatus
  });
});

const filteredData = computed(() => {
  let filtered = trialsStore.trials.filter((trial) => {
    // Only apply client-side search filtering
    // Status filtering is handled by the API, so don't filter again here
    if (props.search) {
      return (
        complexSearch(props.search, trial.school_name) ||
        complexSearch(props.search, trial.district_name) ||
        complexSearch(props.search, trial.email) ||
        complexSearch(props.search, trial.username) ||
        complexSearch(props.search, trial.firstname) ||
        complexSearch(props.search, trial.lastname)
      );
    }
    return true;
  });

  // Apply sorting if sortKey is set
  if (sortKey.value) {
    filtered = filtered.sort((a, b) => {
      let aValue = a[sortKey.value as keyof TrialMembership];
      let bValue = b[sortKey.value as keyof TrialMembership];

      // Handle undefined values
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return sortOrder.value === 'asc' ? 1 : -1;
      if (bValue === undefined) return sortOrder.value === 'asc' ? -1 : 1;

      // Handle date sorting
      if (sortKey.value === 'created_tstamp') {
        aValue = new Date(aValue as string).getTime();
        bValue = new Date(bValue as string).getTime();
      }

      // Handle string sorting (case-insensitive)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortOrder.value === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortOrder.value === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  return filtered;
});

const { currPage, perPage, pageStatusText, totalPages, pageDown, pageUp, paginatedResults } =
  usePaginate(filteredData);

const formatDate = (date: string) => {
  return dayjs(date).format('MM/DD/YYYY HH:mm');
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'requested': return 'info';
    case 'fullfilled': return 'success'; // Note: database has typo 'fullfilled'
    case 'expired': return 'error';
    case 'cancelled': return 'error';
    case 'rejected': return 'error';
    case 'pending': return 'warning';
    default: return 'default';
  }
};

const sortBy = (key: string) => {
  if (sortKey.value === key) {
    // If clicking the same column, toggle order
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    // If clicking a different column, set it as the new sort key with ascending order
    sortKey.value = key;
    sortOrder.value = 'asc';
  }
};

const reload = () => {
  trialsStore.loadTrials({
    status: props.status
  });
};

const editTrial = (trial: TrialMembership) => {
  selectedTrial.value = { ...trial }; // Create a copy, not a reference
  showEditModal.value = true;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;

.icon-col {
	width: 0px;
}

.sortable-header {
	cursor: pointer;
	user-select: none;
	transition: background-color 0.2s;

	&:hover {
		background-color: rgba(0, 0, 0, 0.05);
	}
}

.sort-indicator {
	margin-left: 4px;
	display: inline-block;
}

.table-row {
	td {
		transition: background-color 0.3s;
	}
	&:hover {
		td {
			background-color: #f5f5f5;
		}
	}
}
</style>
